import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Users, House, MapPin, Bell, ChatCircle, ForkKnife, Cake, Plus,
  PaperPlaneRight, X, CheckCircle, Clock, Crown, Baby, ShieldCheck, Warning,
} from '@phosphor-icons/react';

/**
 * Family Tree dashboard widget — beautiful avatar grid showing every household
 * member with role, home/away status, today's tasks, allergies, and a hover
 * quick-action menu (assign reminder, send message, view meal plan).
 */
export default function FamilyTreeWidget() {
  const { api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMember, setOpenMember] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null); // 'remind' | 'message'
  const [drawerForm, setDrawerForm] = useState({ title: '', message: '', text: '', trigger_time: '09:00' });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const fetchOverview = async () => {
    try {
      const res = await api.get('/members/family/overview');
      setOverview(res.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    fetchOverview();
    const t = setInterval(fetchOverview, 60_000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const togglePresence = async (member) => {
    try {
      await api.post(`/members/${member.member_id}/presence`, { is_home: !member.is_home });
      fetchOverview();
    } catch (e) { /* ignore */ }
  };

  const openDrawer = (member, mode) => {
    setOpenMember(member);
    setDrawerMode(mode);
    setDrawerForm({
      title: mode === 'remind' ? `Reminder for ${member.name}` : '',
      message: '',
      text: '',
      trigger_time: '09:00',
    });
  };

  const closeDrawer = () => {
    setOpenMember(null);
    setDrawerMode(null);
  };

  const submitAction = async () => {
    if (!openMember) return;
    setSubmitting(true);
    try {
      const payload = drawerMode === 'remind'
        ? {
            action: 'remind',
            title: drawerForm.title,
            message: drawerForm.message,
            trigger_date: new Date().toISOString().split('T')[0],
            trigger_time: drawerForm.trigger_time,
          }
        : { action: 'message', text: drawerForm.text };
      await api.post(`/members/${openMember.member_id}/quick-action`, payload);
      setToast(drawerMode === 'remind' ? `Reminder set for ${openMember.name}` : `Message sent to ${openMember.name}`);
      setTimeout(() => setToast(''), 2200);
      closeDrawer();
      fetchOverview();
    } catch (e) {
      setToast('Failed: ' + (e.response?.data?.detail || e.message));
      setTimeout(() => setToast(''), 2500);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="glass-card p-6">
        <div className="h-32 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const members = overview?.members || [];
  const pendingInvites = overview?.pending_invites || [];

  return (
    <div className="glass-card p-5 sm:p-6 relative" data-testid="family-tree-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF6B35]/20 to-[#FF8E53]/20 flex items-center justify-center">
            <Users size={18} weight="duotone" className="text-[#FF6B35]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-white text-base sm:text-lg">Family</h3>
            <p className="text-[10px] text-neutral-500">
              {overview?.home_count || 0} home · {(overview?.total || 0) - (overview?.home_count || 0)} away · {overview?.today_tasks_count || 0} tasks today
            </p>
          </div>
        </div>
        <Link to="/members" data-testid="family-tree-manage-link"
          className="text-[11px] text-neutral-500 hover:text-white flex items-center gap-1">
          Manage <Plus size={12} />
        </Link>
      </div>

      {/* Empty state */}
      {members.length === 0 && pendingInvites.length === 0 ? (
        <div className="text-center py-10" data-testid="family-tree-empty">
          <Users size={36} weight="duotone" className="mx-auto text-neutral-600 mb-3" />
          <p className="text-sm text-neutral-500 mb-4">No family members yet</p>
          <Link to="/members"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold">
            <Plus size={12} weight="bold" /> Add or invite family
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {members.map(m => (
            <FamilyAvatar key={m.member_id} member={m} onTogglePresence={togglePresence}
              onAction={(mode) => openDrawer(m, mode)} />
          ))}
          {pendingInvites.map(inv => (
            <PendingAvatar key={inv.invite_id} invite={inv} />
          ))}
        </div>
      )}

      {/* Quick action drawer */}
      {openMember && drawerMode && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
          onClick={closeDrawer}
          data-testid="family-action-drawer">
          <div className="w-full sm:max-w-md rounded-3xl overflow-hidden"
            style={{ background: 'rgba(20,20,20,0.97)', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar member={openMember} size={40} />
                <div>
                  <p className="text-white font-bold text-sm">{openMember.name}</p>
                  <p className="text-[10px] text-neutral-500">
                    {drawerMode === 'remind' ? 'Set a reminder' : 'Send a message'}
                  </p>
                </div>
              </div>
              <button onClick={closeDrawer} className="text-neutral-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {drawerMode === 'remind' ? (
                <>
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Title</label>
                    <input value={drawerForm.title} onChange={e => setDrawerForm(f => ({ ...f, title: e.target.value }))}
                      data-testid="family-reminder-title"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/8 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Message</label>
                    <textarea value={drawerForm.message} onChange={e => setDrawerForm(f => ({ ...f, message: e.target.value }))} rows={2}
                      data-testid="family-reminder-message" placeholder="e.g., Remember to take medicine"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/8 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Time today</label>
                    <input type="time" value={drawerForm.trigger_time} onChange={e => setDrawerForm(f => ({ ...f, trigger_time: e.target.value }))}
                      data-testid="family-reminder-time"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/8 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Message</label>
                  <textarea value={drawerForm.text} onChange={e => setDrawerForm(f => ({ ...f, text: e.target.value }))} rows={4}
                    autoFocus data-testid="family-message-text" placeholder="Type your message..."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/8 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
                  <p className="text-[10px] text-neutral-500 mt-1">Posts to family chat with @{openMember.name} mention</p>
                </div>
              )}
              <button onClick={submitAction} disabled={submitting || (drawerMode === 'message' && !drawerForm.text.trim())}
                data-testid="family-action-submit"
                className="w-full py-3 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #FF6B35, #FF8E53)', boxShadow: '0 6px 20px rgba(255,107,53,0.35)' }}>
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : drawerMode === 'remind' ? <Bell size={14} weight="fill" /> : <PaperPlaneRight size={14} weight="fill" />}
                {drawerMode === 'remind' ? 'Set Reminder' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-full bg-[#FF6B35] text-white text-xs font-semibold shadow-2xl flex items-center gap-2"
          data-testid="family-action-toast">
          <CheckCircle size={14} weight="fill" /> {toast}
        </div>
      )}
    </div>
  );
}

const RELATION_COLORS = {
  Self: '#FF6B35', Spouse: '#EC4899', Father: '#3B82F6', Mother: '#A855F7',
  Son: '#22C55E', Daughter: '#F59E0B', Brother: '#00E5FF', Sister: '#F472B6',
  Grandfather: '#8B5CF6', Grandmother: '#D946EF',
};

function FamilyAvatar({ member, onTogglePresence, onAction }) {
  const color = RELATION_COLORS[member.relation] || '#737373';
  const isAdmin = member.auth_role === 'admin';
  const isChild = (member.auth_role === 'child') || (member.relation === 'Son' || member.relation === 'Daughter');
  const initials = member.name?.[0]?.toUpperCase() || '?';
  const allergies = (member.allergies || '').trim();
  const tasksCount = (member.tasks_today || []).length;

  return (
    <div className="relative group" data-testid={`family-avatar-${member.member_id}`}>
      {/* Card */}
      <div className="rounded-2xl p-3 transition-all hover:scale-[1.02] cursor-default"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${member.is_home ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.05)'}`,
          boxShadow: member.is_home ? '0 0 0 0 rgba(34,197,94,0.4), 0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.2)',
        }}>
        {/* Avatar with status ring */}
        <div className="relative flex items-center justify-center mb-2">
          <button onClick={() => onTogglePresence(member)}
            data-testid={`presence-toggle-${member.member_id}`}
            className="relative rounded-full transition-transform hover:scale-105 active:scale-95"
            title={member.is_home ? 'Currently home — click to mark away' : 'Currently away — click to mark home'}>
            <Avatar member={member} size={56} color={color} initials={initials} />
            {/* Presence dot */}
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-[#0A0A0A]"
              style={{
                background: member.is_home ? '#22C55E' : '#525252',
                boxShadow: member.is_home ? '0 0 8px rgba(34,197,94,0.6)' : 'none',
              }} />
            {isAdmin && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF6B35] flex items-center justify-center"
                title="Admin">
                <Crown size={10} weight="fill" className="text-white" />
              </span>
            )}
          </button>
        </div>

        {/* Name + relation */}
        <p className="text-xs font-bold text-white text-center truncate">{member.name}</p>
        <p className="text-[9px] uppercase tracking-wider text-center font-semibold mt-0.5"
          style={{ color }}>
          {member.relation || (isChild ? 'Child' : 'Member')}
        </p>

        {/* Status row */}
        <div className="flex items-center justify-center gap-1 mt-2 flex-wrap">
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1"
            style={{
              background: member.is_home ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
              color: member.is_home ? '#22C55E' : '#737373',
            }}>
            {member.is_home ? <House size={9} weight="fill" /> : <MapPin size={9} weight="fill" />}
            {member.is_home ? 'Home' : 'Away'}
          </span>
          {tasksCount > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1"
              style={{ background: 'rgba(255,107,53,0.12)', color: '#FF6B35' }}>
              <Bell size={9} weight="fill" /> {tasksCount}
            </span>
          )}
          {allergies && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1"
              style={{ background: 'rgba(239,68,68,0.12)', color: '#F87171' }}
              title={`Allergies: ${allergies}`}>
              <Warning size={9} weight="fill" />
            </span>
          )}
        </div>

        {/* Last seen */}
        {member.last_seen_at && (
          <p className="text-[9px] text-neutral-600 text-center mt-1.5 truncate">
            <Clock size={8} className="inline mr-0.5" />{formatRelative(member.last_seen_at)}
          </p>
        )}
      </div>

      {/* Hover quick-action menu */}
      <div className="absolute inset-x-0 -bottom-2 translate-y-full opacity-0 group-hover:opacity-100 group-hover:translate-y-1 transition-all duration-200 z-20 pointer-events-none group-hover:pointer-events-auto">
        <div className="rounded-xl p-1 flex justify-center gap-1 mx-auto w-fit"
          style={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 12px 30px rgba(0,0,0,0.5)' }}>
          <ActionBtn icon={<Bell size={13} weight="fill" />} label="Remind"
            color="#FF6B35" onClick={() => onAction('remind')} testid={`action-remind-${member.member_id}`} />
          <ActionBtn icon={<ChatCircle size={13} weight="fill" />} label="Message"
            color="#00E5FF" onClick={() => onAction('message')} testid={`action-message-${member.member_id}`} />
          <Link to="/meals" data-testid={`action-meal-${member.member_id}`}
            className="px-2.5 py-1.5 rounded-lg flex items-center gap-1 text-[10px] font-bold text-white hover:bg-white/10 transition-colors"
            title={`${member.name}'s meal plan`}>
            <ForkKnife size={13} weight="fill" className="text-[#22C55E]" />
            <span className="hidden sm:inline">Meal</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-2.5 py-1.5 rounded-lg flex items-center gap-1 text-[10px] font-bold text-white hover:bg-white/10 transition-colors"
      title={label}>
      <span style={{ color }}>{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function PendingAvatar({ invite }) {
  return (
    <Link to="/members" data-testid={`pending-avatar-${invite.invite_id}`}
      className="rounded-2xl p-3 transition-all hover:scale-[1.02] block"
      style={{
        background: 'rgba(0,229,255,0.04)',
        border: '1px dashed rgba(0,229,255,0.35)',
      }}>
      <div className="flex items-center justify-center mb-2">
        <div className="relative w-14 h-14 rounded-full bg-[#00E5FF]/10 flex items-center justify-center">
          <Clock size={22} weight="duotone" className="text-[#00E5FF]" />
        </div>
      </div>
      <p className="text-xs font-bold text-white text-center truncate">{invite.accepted_name}</p>
      <p className="text-[9px] uppercase tracking-wider text-center font-semibold text-[#00E5FF] mt-0.5">
        Awaiting Approval
      </p>
      <p className="text-[9px] text-neutral-500 text-center mt-1.5">{invite.role}</p>
    </Link>
  );
}

function Avatar({ member, size = 48, color, initials }) {
  const c = color || RELATION_COLORS[member.relation] || '#737373';
  const ini = initials || (member.name?.[0]?.toUpperCase() || '?');
  if (member.avatar) {
    return (
      <img src={member.avatar} alt={member.name}
        className="rounded-full object-cover"
        style={{ width: size, height: size, border: `2px solid ${c}40` }} />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center font-bold"
      style={{
        width: size, height: size,
        background: `linear-gradient(135deg, ${c}30, ${c}15)`,
        border: `2px solid ${c}50`,
        color: c,
        fontSize: size * 0.42,
      }}>
      {ini}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
}
